import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, extractApiError } from '../lib/api';
import { DEFAULT_CENTER, getCurrentLocation, type LatLng } from '../lib/geo';
import { MapPicker } from '../components/MapPicker';
import { PhotoPicker } from '../components/PhotoPicker';
import type { CompressedPhoto } from '../lib/image';

interface DutyType {
  id: string;
  code: string;
  name: string;
}

export function DutyStartPage() {
  const navigate = useNavigate();
  const [dutyTypes, setDutyTypes] = useState<DutyType[]>([]);
  const [dutyTypeId, setDutyTypeId] = useState<string>('');
  const [locationName, setLocationName] = useState('');
  const [pos, setPos] = useState<LatLng>(DEFAULT_CENTER);
  const [note, setNote] = useState('');
  const [photos, setPhotos] = useState<CompressedPhoto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [locating, setLocating] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get<DutyType[]>('/app/duty-types').then((r) => {
      setDutyTypes(r.data);
      if (r.data[0]) setDutyTypeId(r.data[0].id);
    });
    // auto-fetch current location on mount
    void locate();
  }, []);

  async function locate() {
    setLocating(true);
    setError(null);
    try {
      const ll = await getCurrentLocation();
      setPos(ll);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'ระบุพิกัดไม่สำเร็จ');
    } finally {
      setLocating(false);
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!dutyTypeId) {
      setError('กรุณาเลือกประเภทงาน');
      return;
    }
    if (!locationName.trim()) {
      setError('กรุณากรอกชื่อสถานที่');
      return;
    }
    if (photos.length < 1) {
      setError('กรุณาแนบรูปยืนยันการเริ่มงานอย่างน้อย 1 รูป');
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append(
        'payload',
        JSON.stringify({
          dutyTypeId: Number(dutyTypeId),
          locationName: locationName.trim(),
          latitude: pos.lat,
          longitude: pos.lng,
          ...(note.trim() ? { note: note.trim() } : {}),
        }),
      );
      photos.forEach((p, i) => fd.append('photos', p.blob, `start-${i}.jpg`));
      await api.post('/app/duty-logs', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      navigate('/duty/active', { replace: true });
    } catch (err) {
      setError(extractApiError(err).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto min-h-screen max-w-md bg-gray-50">
      <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-gray-200 bg-white px-4 py-3">
        <button onClick={() => navigate(-1)} className="text-2xl text-gray-500">
          ‹
        </button>
        <h1 className="text-base font-semibold">เริ่มบันทึกการปฏิบัติงาน</h1>
      </header>

      <form onSubmit={submit} className="space-y-4 p-4">
        <div className="card">
          <label className="label">ประเภทงาน *</label>
          <select
            className="input"
            value={dutyTypeId}
            onChange={(e) => setDutyTypeId(e.target.value)}
          >
            {dutyTypes.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </div>

        <div className="card space-y-3">
          <div>
            <label className="label">สถานที่ปฏิบัติงาน *</label>
            <input
              className="input"
              value={locationName}
              onChange={(e) => setLocationName(e.target.value)}
              placeholder="เช่น แยกบ้านโป่ง, หน้าวัดพระปฐมเจดีย์"
              required
              maxLength={255}
            />
          </div>
          <button
            type="button"
            onClick={locate}
            className="btn-secondary"
            disabled={locating}
          >
            {locating ? 'กำลังระบุพิกัด...' : '📍 ดึงพิกัดปัจจุบัน'}
          </button>
          <MapPicker value={pos} onChange={setPos} />
        </div>

        <div className="card">
          <label className="label">รูปยืนยันการเริ่มงาน *</label>
          <PhotoPicker
            max={2}
            required
            value={photos}
            onChange={setPhotos}
            hint="ถ่ายภาพสถานที่/ตัวเอง — อย่างน้อย 1 รูป สูงสุด 2 รูป"
          />
        </div>

        <div className="card">
          <label className="label">หมายเหตุ</label>
          <textarea
            className="textarea"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="รายละเอียดเพิ่มเติม (ไม่จำเป็น)"
            maxLength={2000}
          />
        </div>

        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <button type="submit" className="btn-primary" disabled={submitting}>
          {submitting ? 'กำลังบันทึก...' : '▶ เริ่มงาน'}
        </button>
      </form>
    </div>
  );
}
